
import React, { useState, useEffect, useMemo } from 'react';
import { subscribeToReviews, addReview, subscribeToRouteReviews, addRouteReview, getThumbUrl, logFeatureUsage, fetchGoogleReviewsForSpot } from '../services/firebaseService';
import type { GoogleReview, GoogleAiSuitability } from '../services/firebaseService';
import { UserReview, AppUser } from '../types';
import { Star, MessageSquare, Camera, Send, Loader2, User, Share2, Link as LinkIcon, ExternalLink, Trophy, MapPin, ChevronRight, Search, X, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TierBadge from './gamification/TierBadge';
import UserAvatar from './common/UserAvatar';
import VehicleBadge from './common/VehicleBadge';
import { useToast } from '../contexts/ToastContext';
import { emitRewardEvent } from '../services/rewardEvents';
import { ImageLightbox } from './ui/ImageLightbox';

interface ReviewSystemProps {
  spotId?: string;
  spotName?: string;
  routeId?: string;
  user: AppUser | null;
  onOpenAuth: () => void;
  spotIsVerified?: boolean;
  // false = plassen mangler ekte brukerbilde — første bilde i en anmeldelse gir førstemann-bonus.
  spotHasPhoto?: boolean;
  onOpenVerification?: () => void;
  onEnsurePersisted?: () => void;
}

const ReviewSystem: React.FC<ReviewSystemProps> = ({ spotId, spotName, routeId, user, onOpenAuth, spotIsVerified = true, spotHasPhoto, onOpenVerification, onEnsurePersisted }) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [reviews, setReviews] = useState<UserReview[]>([]);
  const [googleReviews, setGoogleReviews] = useState<GoogleReview[]>([]);
  const [googleMapsUri, setGoogleMapsUri] = useState<string | null>(null);
  const [aiSuitability, setAiSuitability] = useState<GoogleAiSuitability | null>(null);
  // Google-aggregat (rating + antall) for kombinert stjerne-sammendrag. Kun aggregatet
  // caches/vises — anmeldelsestekst hentes live, se under.
  const [googleAgg, setGoogleAgg] = useState<{ rating: number; count: number } | null>(null);
  // Google-anmeldelser hentes live og kan ta et par sekunder → vis en laste-animasjon.
  const [googleLoading, setGoogleLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(5);
  const [photos, setPhotos] = useState<File[]>([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const targetId = spotId || routeId || '';
  const isRouteReview = !!routeId;

  useEffect(() => {
    if (!targetId) return;
    const unsub = isRouteReview
      ? subscribeToRouteReviews(targetId, setReviews)
      : subscribeToReviews(targetId, setReviews);
    return () => unsub();
  }, [targetId, isRouteReview]);

  // Google-anmeldelser hentes LIVE (aldri lagret) og flettes inn i samme liste.
  useEffect(() => {
    if (!spotId || isRouteReview) { setGoogleReviews([]); setGoogleMapsUri(null); setAiSuitability(null); setGoogleAgg(null); setGoogleLoading(false); return; }
    let cancelled = false;
    setGoogleLoading(true);
    fetchGoogleReviewsForSpot(spotId)
      .then(res => { if (!cancelled) {
        setGoogleReviews(res.reviews);
        setGoogleMapsUri(res.googleMapsUri);
        setAiSuitability(res.aiSuitability);
        setGoogleAgg(typeof res.rating === 'number' && res.rating > 0 ? { rating: res.rating, count: res.userRatingCount || 0 } : null);
      } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setGoogleLoading(false); });
    return () => { cancelled = true; };
  }, [spotId, isRouteReview]);

  // Vis kun de siste 3 først; nullstill når vi bytter plass/rute.
  useEffect(() => { setVisibleCount(3); }, [targetId]);

  const trimmedVideoUrl = videoUrl.trim();
  const hasVideoUrl = trimmedVideoUrl.length > 0;
  const isVideoUrlValid = !hasVideoUrl || isValidHttpUrl(trimmedVideoUrl);
  const canWriteReview = isRouteReview || spotIsVerified;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      onOpenAuth();
      return;
    }
    if (!comment) return;
    if (!isVideoUrlValid) return;
    if (!canWriteReview) return;

    setIsSubmitting(true);
    try {
      onEnsurePersisted?.();
      if (isRouteReview) {
        await addRouteReview(targetId, user, rating, comment, photos.length > 0 ? photos : undefined, hasVideoUrl ? trimmedVideoUrl : undefined);
      } else {
        await addReview(targetId, user, rating, comment, photos.length > 0 ? photos : undefined, hasVideoUrl ? trimmedVideoUrl : undefined, spotName);
      }
      logFeatureUsage('review_write');
      const firstPhoto = !isRouteReview && spotHasPhoto === false && photos.length > 0;
      emitRewardEvent({
        kind: 'write_review',
        title: isRouteReview ? t('reviews.title') : (spotName || t('reviews.title')),
        message: isRouteReview
          ? 'Du hjelper andre å vurdere om ruten passer dem.'
          : firstPhoto
            ? 'Du la inn det aller første bildet av plassen — det gjør den langt mer attraktiv for andre.'
            : 'Du gjør plassen lettere å vurdere for andre brukere.',
        rewardPoints: firstPhoto ? 50 : photos.length > 0 ? 25 : 15,
      });
      setComment('');
      setPhotos([]);
      setVideoUrl('');
      setShowForm(false);
    } catch (err) {
      console.error("Feil ved lagring av anmeldelse:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toMs = (v: any): number => {
    if (!v) return 0;
    if (typeof v === 'number') return v;
    if (typeof v === 'string') { const p = Date.parse(v); return isNaN(p) ? 0 : p; }
    if (typeof v?.toDate === 'function') return v.toDate().getTime();
    if (typeof v?.seconds === 'number') return v.seconds * 1000;
    return 0;
  };

  type DisplayReview = UserReview & { _google?: { authorUri?: string; mapsUri?: string | null; relativeTime?: string }; _sortMs: number };
  const displayReviews: DisplayReview[] = useMemo(() => {
    const native = reviews.map(r => ({ ...r, _sortMs: toMs((r as any).createdAt) })) as DisplayReview[];
    const google = googleReviews.map((g, i) => ({
      id: `google-${i}`,
      userId: 'google',
      userName: g.author || t('spot.google_reviews.anonymous', 'Google-bruker'),
      userPhoto: g.authorPhoto,
      rating: g.rating || 0,
      comment: g.text,
      createdAt: g.publishTime || 0,
      _google: { authorUri: g.authorUri, mapsUri: googleMapsUri, relativeTime: g.relativeTime },
      _sortMs: toMs(g.publishTime),
    })) as DisplayReview[];
    // Fletter våre + Googles omtaler, sortert på dato med nyeste først.
    return [...native, ...google].sort((a, b) => b._sortMs - a._sortMs);
  }, [reviews, googleReviews, googleMapsUri, t]);

  // Kombinert stjerne-snitt (brukeranmeldelser + Google-aggregat), samme sammenslåing
  // som backend (functions/rating.js). Vist rått til brukeren; kartets tier bruker en
  // bayesiansk vektet variant av dette.
  const ratingSummary = useMemo(() => {
    const nativeRated = reviews
      .map(r => Number(r.rating))
      .filter(r => Number.isFinite(r) && r >= 1 && r <= 5);
    let sum = nativeRated.reduce((a, b) => a + b, 0);
    let count = nativeRated.length;
    if (googleAgg && googleAgg.rating >= 1 && googleAgg.rating <= 5 && googleAgg.count > 0) {
      sum += googleAgg.rating * googleAgg.count;
      count += googleAgg.count;
    }
    if (count === 0) return null;
    return { avg: sum / count, count };
  }, [reviews, googleAgg]);

  return (
    <div className="space-y-6 mt-8">
      <div className="flex items-center justify-between border-b border-[var(--border-secondary)] pb-4">
        <h3 className="text-xs font-black text-[var(--text-primary)] uppercase tracking-widest flex items-center gap-2">
          <MessageSquare size={14} /> {t('reviews.title')}
          {displayReviews.length > 0 && (
            <span className="text-[var(--text-muted)] font-black tabular-nums" title={t('reviews.count_hint', 'Antall registrerte omtaler')}>
              ({displayReviews.length})
            </span>
          )}
        </h3>
        {/* Kun vis knappen hvis brukeren er logget inn */}
        {user && (
          <button
            onClick={(e) => { e.stopPropagation(); setShowForm(!showForm); }}
            disabled={!canWriteReview}
            className="text-3xs font-black text-emerald-400 border border-emerald-500/20 bg-emerald-500/5 px-3 py-1.5 rounded-lg hover:bg-emerald-500/10 transition-all"
          >
            {showForm
              ? t('reviews.cancel_btn')
              : t('reviews.write_btn')
            }
          </button>
        )}
      </div>

      {/* Kombinert stjerne-snitt fra ekte anmeldelser (bruker + Google). Rått snitt vises;
          kartmarkørens tier bruker en bayesiansk vektet variant. */}
      {ratingSummary && (
        <div className="flex items-center gap-2 -mt-3">
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(s => (
              <Star key={s} size={16}
                fill={s <= Math.round(ratingSummary.avg) ? 'currentColor' : 'none'}
                className={s <= Math.round(ratingSummary.avg) ? 'text-amber-400' : 'text-[var(--text-muted)]'} />
            ))}
          </div>
          <span className="text-sm font-black text-[var(--text-primary)] tabular-nums">{ratingSummary.avg.toFixed(1)}</span>
          <span className="text-2xs font-bold text-[var(--text-muted)]">
            ({ratingSummary.count} {t('reviews.rating_count_label', 'vurderinger')})
          </span>
        </div>
      )}

      {/* AI-vurdert høy sikkerhet — synlig heving av tillit uten å kreditere Google.
          Kun for ikke-menneskeverifiserte plasser (menneske-«Bekreftet» vinner). */}
      {!spotIsVerified && aiSuitability?.suitable && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 flex items-start gap-3">
          <ShieldCheck size={18} className="text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-1 min-w-0">
            <p className="text-2xs font-black uppercase tracking-widest text-emerald-300 flex items-center gap-1">
              {t('reviews.ai_suitable_title', 'Høy sikkerhet')}
              {aiSuitability.scoreBoost > 0 && (
                <span className="text-emerald-400/90">↑ +{aiSuitability.scoreBoost}</span>
              )}
            </p>
            {aiSuitability.summary && (
              <p className="text-tiny text-emerald-100/90 leading-relaxed">{aiSuitability.summary}</p>
            )}
          </div>
        </div>
      )}

      {!spotIsVerified && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 space-y-3">
          <p className="text-2xs font-black uppercase tracking-widest text-amber-300">
            {t('reviews.not_verified_title')}
          </p>
          <p className="text-tiny text-amber-100/90 leading-relaxed">
            {t('reviews.not_verified_desc')}
          </p>
          {user && onOpenVerification && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenVerification(); }}
              className="bg-amber-500 text-black px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest hover:bg-amber-400 transition-colors"
            >
              {t('reviews.verify_btn')}
            </button>
          )}
        </div>
      )}

      {showForm && canWriteReview && (
        <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()} className="bg-[var(--bg-input)]/50 p-6 rounded-3xl border border-[var(--border-secondary)] space-y-4 animate-in slide-in-from-top-4">
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className={`transition-all ${star <= rating ? 'text-amber-400 scale-110' : 'text-[var(--text-muted)]'}`}
              >
                <Star size={24} fill={star <= rating ? 'currentColor' : 'none'} />
              </button>
            ))}
          </div>

          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('reviews.placeholder')}
            className="w-full bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-2xl p-4 text-xs text-[var(--text-primary)] focus:outline-none focus:border-emerald-500 min-h-[100px] resize-none"
          />

          <div className="space-y-3">
            {photos.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {photos.map((file, idx) => {
                  const previewUrl = URL.createObjectURL(file);
                  return (
                    <div key={idx} className="relative group">
                      <img
                        src={previewUrl}
                        alt={file.name}
                        className="w-16 h-16 object-cover rounded-xl border border-stone-700"
                        onLoad={() => URL.revokeObjectURL(previewUrl)}
                      />
                      <button
                        type="button"
                        onClick={() => setPhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-2xs sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex items-center justify-between">
              <label className={`flex items-center gap-2 cursor-pointer transition-colors text-2xs font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] ${photos.length >= 5 ? 'opacity-40 pointer-events-none' : ''}`}>
                <Camera size={16} />
                <span>{photos.length > 0 ? t('reviews.add_more_photos') : t('reviews.add_photos')}</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files || []);
                    setPhotos(prev => [...prev, ...newFiles].slice(0, 5));
                    e.target.value = '';
                  }}
                />
              </label>
              {photos.length > 0 && (
                <span className="text-3xs text-[var(--text-muted)] font-bold">{photos.length}/5</span>
              )}
            </div>
          </div>

          <div>
            <label className="text-2xs font-bold text-[var(--text-secondary)] flex items-center gap-2 mb-1">
              <LinkIcon size={14} />
              {t('reviews.add_video_link')}
            </label>
            <input
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder={t('reviews.video_link_placeholder')}
              className={`w-full bg-[var(--bg-surface)] border rounded-xl px-3 py-2 text-2xs text-[var(--text-primary)] focus:outline-none ${isVideoUrlValid ? 'border-[var(--border-secondary)] focus:border-emerald-500' : 'border-red-500/80 focus:border-red-500'}`}
            />
            {!isVideoUrlValid && (
              <p className="mt-1 text-3xs font-bold text-red-400">{t('reviews.video_link_invalid')}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-2xs text-[var(--text-muted)] font-bold" />
            <button
              disabled={isSubmitting || !comment || !isVideoUrlValid}
              className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-black text-2xs uppercase tracking-widest flex items-center gap-2 hover:bg-emerald-500 disabled:opacity-50 transition-all disabled:cursor-not-allowed"
              title=""
            >
              {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {t('reviews.publish')}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {displayReviews.length === 0 && googleLoading ? (
          <div className="space-y-4" role="status" aria-live="polite">
            <div className="flex items-center gap-2 text-2xs font-bold text-[var(--text-muted)] py-1">
              <Loader2 size={14} className="animate-spin text-sky-400" />
              <span>{t('reviews.loading_google', 'Henter omtaler fra Google …')}</span>
            </div>
            {[0, 1, 2].map(i => (
              <div key={i} className="bg-[var(--bg-input)]/30 border border-[var(--border-secondary)]/50 p-5 rounded-panel space-y-3 animate-pulse">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[var(--bg-surface-alt)]" />
                  <div className="space-y-1.5">
                    <div className="h-2.5 w-24 rounded bg-[var(--bg-surface-alt)]" />
                    <div className="h-2 w-16 rounded bg-[var(--bg-surface-alt)]" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="h-2 w-full rounded bg-[var(--bg-surface-alt)]" />
                  <div className="h-2 w-4/5 rounded bg-[var(--bg-surface-alt)]" />
                </div>
              </div>
            ))}
          </div>
        ) : displayReviews.length === 0 ? (
          <p className="text-[var(--text-muted)] text-2xs font-bold italic py-4">{t('reviews.no_reviews')} {user ? t('reviews.be_first') : t('reviews.login_first')}</p>
        ) : (
          displayReviews.slice(0, visibleCount).map((rev) => {
            const g = rev._google;
            return (
            <div key={rev.id} className="bg-[var(--bg-input)]/30 border border-[var(--border-secondary)]/50 p-5 rounded-panel space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <UserAvatar
                    photoURL={rev.userPhoto}
                    tier={rev.userTier}
                    size="sm"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-2xs font-black text-[var(--text-primary)]">{rev.userName}</p>
                      {g ? (
                        <span className="inline-flex items-center gap-1 text-4xs font-bold text-[var(--text-muted)] bg-[var(--bg-surface-alt)] px-1.5 py-0.5 rounded-full" title={t('spot.google_reviews.title', 'Anmeldelse fra Google')}>Google</span>
                      ) : (<>
                        {rev.userTier !== undefined && rev.userTier > 0 && (
                          <TierBadge tier={rev.userTier} className="scale-75 origin-left opacity-80" />
                        )}
                        {rev.vehicleCategory && (
                          <VehicleBadge category={rev.vehicleCategory} compact />
                        )}
                      </>)}
                    </div>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} size={8} fill={s <= rev.rating ? '#fbbf24' : 'none'} className={s <= rev.rating ? 'text-amber-400' : 'text-[var(--text-muted)]'} />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  {g ? (
                    <a
                      href={g.mapsUri || g.authorUri || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 text-[var(--text-muted)] hover:text-sky-400 transition-colors"
                      title={t('spot.google_reviews.view_on_maps', 'Se på Google Maps')}
                    >
                      <ExternalLink size={14} />
                    </a>
                  ) : (
                    <button
                      onClick={() => {
                        const url = `${window.location.origin}/share/review/${rev.id}`;
                        if (navigator.share) {
                          navigator.share({
                            title: t('reviews.share_title'),
                            text: t('reviews.share_text'),
                            url: url
                          }).catch(console.error);
                        } else {
                          navigator.clipboard.writeText(url);
                          toast(t('common.link_copied') || 'Link copied!', 'success');
                        }
                      }}
                      className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                      title={t('common.share') || 'Share'}
                    >
                      <Share2 size={14} />
                    </button>
                  )}
                  <span className="text-4xs font-bold text-[var(--text-muted)]">
                    {g ? g.relativeTime : new Date(rev.createdAt).toLocaleDateString(i18n.language === 'no' ? 'no-NO' : 'en-US')}
                  </span>
                </div>
              </div>

              <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">{rev.comment}</p>

              {!g && (rev.photoUrls && rev.photoUrls.length > 0 ? rev.photoUrls : rev.photoUrl ? [rev.photoUrl] : []).length > 0 && (
                <div className={`mt-2 gap-2 ${(rev.photoUrls?.length || 1) > 1 ? 'grid grid-cols-2' : ''}`}>
                  {(rev.photoUrls && rev.photoUrls.length > 0 ? rev.photoUrls : [rev.photoUrl!]).map((url, idx) => (
                    <div key={idx} className="rounded-2xl overflow-hidden border border-[var(--border-secondary)] shadow-xl">
                      <img
                        src={getThumbUrl(url)}
                        alt={t('reviews.user_upload_alt')}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-48 object-cover cursor-pointer hover:scale-105 transition-transform duration-500"
                        onClick={() => {
                          setLightboxImages(rev.photoUrls && rev.photoUrls.length > 0 ? rev.photoUrls : [rev.photoUrl!]);
                          setLightboxIdx(idx);
                        }}
                        onError={(e) => { if ((e.target as HTMLImageElement).src !== url) (e.target as HTMLImageElement).src = url; }}
                      />
                    </div>
                  ))}
                </div>
              )}
              {!g && rev.videoUrl && (
                <a
                  href={rev.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-2 text-2xs font-black uppercase tracking-wider text-sky-300 hover:text-sky-200"
                >
                  <ExternalLink size={12} />
                  {t('reviews.watch_video')}
                </a>
              )}
            </div>
          );})
        )}
        {displayReviews.length > visibleCount && (
          <button
            onClick={() => setVisibleCount(c => c + 3)}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] transition-colors"
          >
            <ChevronRight size={12} className="rotate-90" />
            {t('reviews.show_more', 'Vis flere omtaler')} (+{Math.min(3, displayReviews.length - visibleCount)})
          </button>
        )}
        {displayReviews.length > 0 && googleLoading && (
          <div className="flex items-center justify-center gap-2 text-3xs font-bold text-[var(--text-muted)] py-1" role="status" aria-live="polite">
            <Loader2 size={12} className="animate-spin text-sky-400" />
            <span>{t('reviews.loading_google', 'Henter omtaler fra Google …')}</span>
          </div>
        )}
      </div>

      <ImageLightbox images={lightboxImages} index={lightboxIdx} onIndexChange={setLightboxIdx} />
    </div>
  );
};

export default ReviewSystem;

function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}
